import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";
import { useInView } from "@/hooks/useInView";
import SeoHead from "@/seo/SeoHead";
import { buildAutoRepairJsonLd, buildBreadcrumbJsonLd } from "@/seo/jsonLd";
import { SITE_URL } from "@/seo/siteSeo";
import { useSiteContent } from "@/cms/SiteContentContext";
import { editableAttrs } from "@/cms/previewUtils";
import { resolveMapsHref } from "@/config/maps";

export default function IletisimPage() {
  const { ref, inView } = useInView();
  const { content } = useSiteContent();
  const page = content.contactPage;
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: "Car Garage VAG İletişim",
      url: `${SITE_URL}/iletisim`,
    },
    buildBreadcrumbJsonLd([
      { name: "Ana Sayfa", path: "/" },
      { name: "İletişim", path: "/iletisim" },
    ]),
    buildAutoRepairJsonLd(),
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#09090b" }}>
      <SeoHead title="İletişim" description={page.hero.description} path="/iletisim" image={page.hero.imageSrc} jsonLd={jsonLd} />
      <Navbar />

      <section className="relative pt-36 pb-20 overflow-hidden" {...editableAttrs("contactPage.hero", "object")}>
        <div className="absolute inset-0 z-0">
          <img
            src={page.hero.imageSrc}
            alt="İletişim"
            className="w-full h-full object-cover object-top"
            {...editableAttrs("contactPage.hero.imageSrc", "image")}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-zinc-950/96 via-zinc-950/80 to-zinc-950/55"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/90 via-transparent to-zinc-950/40"></div>
        </div>
        <div className="absolute left-0 top-0 h-full w-1 bg-red-600 z-10"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-2 mb-4 text-sm">
            <Link to="/" className="text-zinc-500 hover:text-red-500 transition-colors cursor-pointer">
              Ana Sayfa
            </Link>
            <span className="text-zinc-700">/</span>
            <span className="text-red-500">İletişim</span>
          </div>
          <span className="text-red-500 text-xs font-semibold tracking-widest uppercase" style={{ fontFamily: "Rajdhani, sans-serif" }}>
            {page.hero.badge}
          </span>
          <h1 className="text-5xl md:text-7xl font-bold text-white mt-3 leading-tight" style={{ fontFamily: "Rajdhani, sans-serif" }}>
            {page.hero.title}
          </h1>
          <p className="text-zinc-300 text-lg mt-5 max-w-xl leading-relaxed">{page.hero.description}</p>
        </div>
      </section>

      <section className="py-20 bg-zinc-950" {...editableAttrs("contactPage.infoCards", "array")}>
        <div className="max-w-7xl mx-auto px-6">
          <div ref={ref} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-14">
            {page.infoCards.map((item, idx) => {
              const actionHref = item.actionHref ? resolveMapsHref(item.actionHref) : undefined;
              return (
              <div
                key={`${item.title}-${idx}`}
                className="bg-zinc-900 border border-zinc-800 hover:border-red-600/50 transition-all duration-300 p-7 flex flex-col"
                style={{
                  opacity: inView ? 1 : 0,
                  transform: inView ? "translateY(0)" : "translateY(24px)",
                  transition: `opacity 0.5s ease ${idx * 0.1}s, transform 0.5s ease ${idx * 0.1}s`,
                }}
              >
                <div className="w-12 h-12 flex items-center justify-center bg-red-600/10 border border-red-600/30 mb-5">
                  <i className={`${item.icon} text-red-500 text-xl`}></i>
                </div>
                <h3 className="text-white font-bold text-lg mb-3" style={{ fontFamily: "Rajdhani, sans-serif" }}>
                  {item.title}
                </h3>
                <div className="flex flex-col gap-1 flex-1">
                  {item.lines.map((line, lineIndex) => (
                    <span key={`${line}-${lineIndex}`} className="text-zinc-300 text-sm">
                      {line}
                    </span>
                  ))}
                </div>
                {item.actionLabel && actionHref ? (
                  <a
                    href={actionHref}
                    target={actionHref.startsWith("http") ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    className="mt-5 flex items-center gap-2 text-red-500 hover:text-red-400 text-sm font-semibold transition-colors cursor-pointer"
                    style={{ fontFamily: "Rajdhani, sans-serif" }}
                  >
                    {item.actionLabel}
                    <i className="ri-arrow-right-line text-sm"></i>
                  </a>
                ) : null}
              </div>
            );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-zinc-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div {...editableAttrs("contactPage.brandsSection", "object")}>
              <span className="text-red-500 text-xs font-semibold tracking-widest uppercase" style={{ fontFamily: "Rajdhani, sans-serif" }}>
                {page.brandsSection.badge}
              </span>
              <h2 className="text-4xl font-bold text-white mt-3 mb-6 section-title-line" style={{ fontFamily: "Rajdhani, sans-serif" }}>
                {page.brandsSection.title}
                <br />
                <span className="text-red-500">{page.brandsSection.titleAccent}</span>
              </h2>
              <p className="text-zinc-400 text-sm leading-relaxed mb-8">{page.brandsSection.description}</p>
              <div className="grid grid-cols-3 gap-3" {...editableAttrs("contactPage.brandsSection.brands", "array")}>
                {page.brandsSection.brands.map((brand, index) => (
                  <div
                    key={`${brand.name}-${index}`}
                    className="bg-zinc-800/60 border border-zinc-700/40 hover:border-red-600/40 transition-colors py-4 px-3 text-center"
                    {...editableAttrs("contactPage.brandsSection.brands", "array")}
                  >
                    <div className="h-7 flex items-center justify-center mb-2">
                      <img
                        src={brand.logo}
                        alt={brand.name}
                        className="max-h-full max-w-[92px] w-auto object-contain"
                        style={{ filter: brand.filter }}
                        loading="lazy"
                        {...editableAttrs(`contactPage.brandsSection.brands.${index}.logo`, "image")}
                      />
                    </div>
                    <span className="text-white text-sm font-bold" style={{ fontFamily: "Rajdhani, sans-serif" }} {...editableAttrs(`contactPage.brandsSection.brands.${index}.name`, "text")}>
                      {brand.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-zinc-800/40 border border-zinc-700/40 p-10" {...editableAttrs("contactPage.quickActions", "object")}>
              <span className="text-red-500 text-xs font-semibold tracking-widest uppercase" style={{ fontFamily: "Rajdhani, sans-serif" }}>
                {page.quickActions.badge}
              </span>
              <h3 className="text-3xl font-bold text-white mt-3 mb-6" style={{ fontFamily: "Rajdhani, sans-serif" }}>
                {page.quickActions.title}
                <br />
                <span className="text-red-500">{page.quickActions.titleAccent}</span>
              </h3>
              <div className="flex flex-col gap-4" {...editableAttrs("contactPage.quickActions.actions", "array")}>
                {page.quickActions.actions.map((action, index) => {
                  const actionHref = resolveMapsHref(action.href);
                  return (
                  <a
                    key={`${action.title}-${index}`}
                    href={actionHref}
                    target={actionHref.startsWith("http") ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    className={`flex items-center gap-4 px-6 py-4 font-bold transition-colors cursor-pointer ${action.className}`}
                    style={{ fontFamily: "Rajdhani, sans-serif" }}
                  >
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                      <i className={`${action.icon} text-lg`}></i>
                    </div>
                    <div>
                      <div className="text-xs tracking-widest uppercase">{action.title}</div>
                      <div className="text-base">{action.value}</div>
                    </div>
                  </a>
                );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
